import {
	Container,
	ContainerGutter,
	ContainerPadding,
	ReferenceBadge,
	Richtext,
	Text,
	TextAlignment,
	TextTag,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { default as React } from 'react';
import { useOfertaCard } from '../../context/ofertas-card-context';
import { OfertasContext } from '../../context/ofertas-context';
import { checkActiveDisclaimer, formatCalculatedDisclaimer } from '../../helpers/get-disclaimer';
import {
	capitalizeSentence,
	formatPrecio,
	getDatosEconomicos,
	getFinanciacionPeriodicidad,
	getFinanciacionType,
	getPagoFinanciado,
} from '../../helpers/ofertas-helper';
import { Oferta } from '../../services/ofertas-service/models/ofertas';
import { StyledText } from './desktop/ui/oferta-card-desktop-styles';

export const OfertaPrice = ({
	oferta,
	brand,
	isMobile = false,
	isActive = true,
}: {
	oferta: Oferta;
	brand: string;
	isMobile?: boolean;
	isActive?: boolean;
}): React.ReactElement => {
	const { handleScrollToDisclaimer } = useOfertaCard();

	const { disclaimerManager } = React.useContext(OfertasContext);

	const selectedDatosEconomicos = getDatosEconomicos(oferta.datosEconomicos);

	return (
		<Container padding={{ top: ContainerPadding.static200 }} verticalAlign={'flex-end'} wrap={'always'}>
			{oferta.calculatedFinance.rawResponse ? (
				<>
					<Text
						textAlign={TextAlignment.end}
						tag={TextTag.span}
						appearance={TokenTextAppearance.copy100}
						color={TokenTextColor.primary}
					>
						Tu opción personalizada
					</Text>

					<Container verticalAlign={'flex-end'} gutter={ContainerGutter.static100}>
						<StyledText
							textAlign={TextAlignment.end}
							tag={TextTag.span}
							appearance={isActive ? TokenTextAppearance.headline200 : TokenTextAppearance.headline100}
							color={TokenTextColor.primary}
						>
							{'Por\u00A0'}
						</StyledText>

						<StyledText
							tag={TextTag.span}
							appearance={isActive ? TokenTextAppearance.headline200 : TokenTextAppearance.headline100}
							color={TokenTextColor.primary}
							bold
						>
							{(oferta.calculatedFinance.summaries?.Full.Rate?.Value || '') +
								getFinanciacionPeriodicidad(oferta.datosEconomicos)}

							<ReferenceBadge
								onClick={() => {
									const ref = checkActiveDisclaimer(
										disclaimerManager.getScopedDisclaimerManager().getFooterDisclaimers().reference,
										formatCalculatedDisclaimer(oferta),
									)?.references?.[0].replace('.', '');
									if (ref) handleScrollToDisclaimer(ref);
								}}
								aria-label="Ir al legal"
							>
								{checkActiveDisclaimer(
									disclaimerManager.getScopedDisclaimerManager().getFooterDisclaimers().reference,
									formatCalculatedDisclaimer(oferta),
								)?.references?.[0].replace('.', '')}
							</ReferenceBadge>
							{'\u00A0'}
						</StyledText>

						<StyledText
							tag={TextTag.span}
							appearance={isActive ? TokenTextAppearance.headline200 : TokenTextAppearance.headline100}
							color={TokenTextColor.primary}
						>
							{'con\u00A0' +
								(getPagoFinanciado(oferta.datosEconomicos)
									? getFinanciacionType(getPagoFinanciado(oferta.datosEconomicos)!, brand)
									: '')}
						</StyledText>
					</Container>
				</>
			) : (
				// Contenedor que incluye todos los precios
				<Container wrap={'always'} verticalAlign={'flex-end'} gutter={ContainerGutter.static100}>
					{selectedDatosEconomicos.map((datoEconomico, index) => {
						return datoEconomico ? (
							<Container
								key={index}
								verticalAlign={'flex-end'}
								data-testid={'datoEconomico' + index}
								wrap={'always'}
								gutter={ContainerGutter.static100}
							>
								{/* Precio y legal que se muestra */}
								<Container wrap={'auto'} verticalAlign={'flex-end'}>
									<StyledText
										tag={TextTag.span}
										appearance={isActive ? TokenTextAppearance.headline200 : TokenTextAppearance.headline100}
										color={TokenTextColor.primary}
									>
										{capitalizeSentence(datoEconomico.modalidad) &&
											`${capitalizeSentence(datoEconomico.modalidad)}\u00A0`}
										<b>
											{datoEconomico.periodicidad == 'pago-unico'
												? formatPrecio(datoEconomico.precio) + '€'
												: parseFloat(datoEconomico?.precio.toFixed(2)).toString().replace('.', ',') +
													getFinanciacionPeriodicidad(oferta.datosEconomicos)}
										</b>
									</StyledText>

									<Text sup bold>
										{datoEconomico.legal && (
											<ReferenceBadge
												onClick={() => {
													const ref = checkActiveDisclaimer(
														disclaimerManager.getScopedDisclaimerManager().getFooterDisclaimers().reference,
														datoEconomico.legal,
													)?.references?.[0].replace('.', '');
													if (ref) handleScrollToDisclaimer(ref);
												}}
												aria-label="Ir al legal"
											>
												{checkActiveDisclaimer(
													disclaimerManager.getScopedDisclaimerManager().getFooterDisclaimers().reference,
													datoEconomico.legal,
												)?.references?.[0].replace('.', '')}
											</ReferenceBadge>
										)}
										{'\u00A0'}
									</Text>

									{datoEconomico.periodicidad !== 'pago-unico' && (
										<StyledText
											tag={TextTag.span}
											appearance={isActive ? TokenTextAppearance.headline200 : TokenTextAppearance.headline100}
											color={TokenTextColor.primary}
											bold
										>
											{'con\u00A0' + getFinanciacionType(datoEconomico, brand)}
										</StyledText>
									)}
								</Container>

								{datoEconomico.periodicidad == 'pago-unico' || datoEconomico.detalleFinanciacion != '' ? (
									<div style={{ display: 'grid' }}>
										<StyledText
											tag={TextTag.span}
											appearance={
												isActive
													? isMobile
														? TokenTextAppearance.label200
														: TokenTextAppearance.label150
													: TokenTextAppearance.label100
											}
											color={TokenTextColor.tertiary}
										>
											{datoEconomico.periodicidad == 'pago-unico' ? (
												<>Sujeto a financiación lineal</>
											) : datoEconomico.detalleFinanciacion != '' ? (
												<Richtext markdown={datoEconomico.detalleFinanciacion} dangerouslyParseRawHTML />
											) : null}
										</StyledText>
									</div>
								) : null}
							</Container>
						) : null;
					})}
				</Container>
			)}
		</Container>
	);
};
